import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { ownerStamp } from '../recordOwner';
import HeaderActionsPortal from './HeaderActionsPortal';
import ModuleEditPermissions from './ModuleEditPermissions';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Table as TableIcon, Plus, Trash2, Clock, Edit2, Save, X, ArrowUp, ArrowDown, Download, Upload, Shield, CalendarDays } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';

const TimetableAdmin = () => {
  const { t } = useT();
  // The stored word, in the reader's language where the platform knows it. The field is free
  // text with a datalist, so a school's own word falls through unchanged.
  const periodTypeWord = (v) => t(
    `myCalendar.periodTypes.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  // Form State
  const [isEditing, setIsEditing] = useState(false);
  const [currentProfile, setCurrentProfile] = useState({ name: '', shortCode: '', isDefault: false, periods: [] });

  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    if (parts.length === 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return timeStr;
  };

  const { activeSchoolId, isMobile, closeSidebar } = useOutletContext();
  const [topTab, setTopTab] = useState('main');

  useEffect(() => {
    if (activeSchoolId) fetchProfiles();
  }, [activeSchoolId]);

  const fetchProfiles = async () => {
    if (!activeSchoolId) return;
    setLoading(true);
    try {
      const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
      const pList = [];
      pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
      setProfiles(pList);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleAddPeriod = () => {
    setCurrentProfile({
      ...currentProfile,
      periods: [...currentProfile.periods, { name: '', startTime: '', endTime: '', type: 'Lesson' }]
    });
  };

  const handleUpdatePeriod = (index, field, value) => {
    const updated = [...currentProfile.periods];
    updated[index][field] = value;
    setCurrentProfile({ ...currentProfile, periods: updated });
  };

  const handleRemovePeriod = (index) => {
    const updated = currentProfile.periods.filter((_, i) => i !== index);
    setCurrentProfile({ ...currentProfile, periods: updated });
  };

  const handleMovePeriod = (index, direction) => {
    if (index + direction < 0 || index + direction >= currentProfile.periods.length) return;
    const updated = [...currentProfile.periods];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    setCurrentProfile({ ...currentProfile, periods: updated });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentProfile.name) return;

    try {
      // If setting this to default, unset other defaults
      if (currentProfile.isDefault) {
        for (let p of profiles) {
          if (p.isDefault && p.uid !== currentProfile.uid) {
            await updateDoc(doc(db, 'schools', activeSchoolId, 'timetable_profiles', p.uid), { isDefault: false });
          }
        }
      }

      if (currentProfile.uid) {
        // Update existing
        await updateDoc(doc(db, 'schools', activeSchoolId, 'timetable_profiles', currentProfile.uid), {
          name: currentProfile.name,
          shortCode: currentProfile.shortCode || '',
          isDefault: currentProfile.isDefault,
          periods: currentProfile.periods
        });
      } else {
        // Create new
        await addDoc(collection(db, 'schools', activeSchoolId, 'timetable_profiles'), { ...ownerStamp(),
          name: currentProfile.name,
          shortCode: currentProfile.shortCode || '',
          schoolId: activeSchoolId,
          isDefault: currentProfile.isDefault,
          periods: currentProfile.periods
        });
      }

      setIsEditing(false);
      setCurrentProfile({ name: '', shortCode: '', isDefault: false, periods: [] });
      fetchProfiles();
    } catch (e) {
      alert(t('timetableAdmin.saveFailed', { message: e.message }));
    }
  };

  const handleDeleteProfile = async (uid) => {
    if (!window.confirm(t('timetableAdmin.deleteConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'timetable_profiles', uid));
      fetchProfiles();
    } catch (e) {
      alert(t('timetableAdmin.deleteFailed', { message: e.message }));
    }
  };

  const handleExportCSV = () => {
    if (profiles.length === 0) return;
    
    const headers = ['Profile Name', 'Short Code', 'Is Default', 'Period Name', 'Start Time', 'End Time', 'Type'];
    const rows = [];
    
    profiles.forEach(p => {
      if (!p.periods || p.periods.length === 0) {
        rows.push([p.name, p.shortCode || '', p.isDefault ? 'TRUE' : 'FALSE', '', '', '', '']);
      } else {
        p.periods.forEach(period => {
          rows.push([
            p.name,
            p.shortCode || '',
            p.isDefault ? 'TRUE' : 'FALSE',
            period.name || '',
            period.startTime || '',
            period.endTime || '',
            period.type || ''
          ]);
        });
      }
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `TimetableProfiles.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target.result;
      const lines = text.split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length < 2) return alert(t('timetableAdmin.invalidCsv'));

      const parseCSVRow = (str) => {
          const arr = [];
          let quote = false, col = 0;
          for (let c = 0; c < str.length; c++) {
              let cc = str[c], nc = str[c+1];
              arr[col] = arr[col] || '';
              if (cc == '"' && quote && nc == '"') { arr[col] += cc; ++c; continue; }
              if (cc == '"') { quote = !quote; continue; }
              if (cc == ',' && !quote) { ++col; continue; }
              arr[col] += cc;
          }
          return arr;
      };
      
      setLoading(true);
      try {
        const groupedProfiles = {};
        
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVRow(lines[i]);
          if (cols.length < 7) continue;
          
          const profileName = cols[0];
          const shortCode = cols[1];
          const isDefault = cols[2].toUpperCase() === 'TRUE';
          const periodName = cols[3];
          const startTime = cols[4];
          const endTime = cols[5];
          const type = cols[6];
          
          if (!groupedProfiles[profileName]) {
            groupedProfiles[profileName] = { name: profileName, shortCode, isDefault, periods: [] };
          }
          
          if (periodName) {
            groupedProfiles[profileName].periods.push({
              name: periodName, startTime, endTime, type
            });
          }
        }
        
        const batch = writeBatch(db);
        let updatesMade = 0;
        let createsMade = 0;
        
        const importingDefault = Object.values(groupedProfiles).some(p => p.isDefault);
        if (importingDefault) {
          profiles.forEach(p => {
             if (p.isDefault && !groupedProfiles[p.name]) {
                batch.update(doc(db, 'schools', activeSchoolId, 'timetable_profiles', p.uid), { isDefault: false });
             }
          });
        }
        
        for (const pName in groupedProfiles) {
          const data = groupedProfiles[pName];
          const existing = profiles.find(p => p.name === pName);
          
          if (existing) {
            batch.update(doc(db, 'schools', activeSchoolId, 'timetable_profiles', existing.uid), data);
            updatesMade++;
          } else {
            const newRef = doc(collection(db, 'schools', activeSchoolId, 'timetable_profiles'));
            batch.set(newRef, { ...data, schoolId: activeSchoolId });
            createsMade++;
          }
        }
        
        await batch.commit();
        alert(t('timetableAdmin.imported', { updated: updatesMade, created: createsMade }));
        fetchProfiles();
      } catch (err) {
        alert(t('timetableAdmin.importFailed', { message: err.message }));
        setLoading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  // Shared dialog behaviour: retract the mobile sidebar (a fixed overlay that would otherwise sit on
  // top of the sheet), stop the page behind from scrolling, and close on Escape.
  useEffect(() => {
    if (!isEditing) return;
    if (isMobile) closeSidebar?.();

    const onKey = (e) => { if (e.key === 'Escape') setIsEditing(false); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isEditing, isMobile, closeSidebar]);

  const openEditor = (profile = { name: '', shortCode: '', isDefault: false, periods: [] }) => {
    setCurrentProfile(profile);
    setIsEditing(true);
  };

  if (loading) {
    return <div className="p-4 text-center text-muted">{t('timetableAdmin.loading')}</div>;
  }

  // Same rule as the calendar's toolbar: icon-only on desktop where the row is tight and a tooltip
  // is one hover away, labelled in the mobile drawer where there is room and no hover to rely on.
  const iconBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
    width: 'auto', padding: isMobile ? '0.5rem 0.9rem' : '0.5rem', minWidth: isMobile ? 0 : '38px',
    whiteSpace: 'nowrap',
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'main', label: t('timetableAdmin.tabs.timetables'), icon: CalendarDays },
            { id: 'settings', label: t('timetableAdmin.tabs.settings'), icon: Shield },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setTopTab(tab.id)}
                className={`theme-tab ${topTab === tab.id ? 'active' : ''}`}
              >
                <div className="theme-tab-icon"><Icon size={20} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* One bar per module. These used to live in a second theme-tabs-bar underneath, whose only
            "tab" was a heading with isActive hardcoded true and no onClick -- a label wearing a tab's
            clothes, saying the same thing as the sidebar item that got you here. The label is gone
            and the controls moved up. They stay put while the editor dialog is open, and are hidden
            on the Settings tab because none of them apply to it. */}
        {topTab === 'main' && (
          <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '0.5rem', padding: '0.5rem 1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="file" accept=".csv" ref={fileInputRef} style={{ display: 'none' }} onChange={handleImportCSV} />
            <button className="btn-outline" onClick={() => fileInputRef.current?.click()}
                    title={t('timetableAdmin.importCsv')} aria-label={t('timetableAdmin.importCsv')} style={iconBtn}>
              <Upload size={16} />{isMobile && ' Import CSV'}
            </button>
            <button className="btn-outline" onClick={handleExportCSV}
                    title={t('timetableAdmin.exportCsv')} aria-label={t('timetableAdmin.exportCsv')} style={iconBtn}>
              <Download size={16} />{isMobile && ' Export CSV'}
            </button>
            <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--color-border)', margin: '0 0.25rem' }}></div>
            {/* Keeps its label: it is the action somebody arrives wanting, and a bare plus next to
                two other icons says nothing about what gets created. */}
            <button className="btn-primary" onClick={() => openEditor()}
                    title={t('timetableAdmin.createProfile')} aria-label={t('timetableAdmin.createProfile')}
                    style={{ ...iconBtn, padding: '0.5rem 0.9rem' }}>
              <Plus size={16} /> Create Profile
            </button>
          </div>
          </HeaderActionsPortal>
        )}
      </div>

      {topTab === 'settings' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 0 : '1.5rem' }}>
          <ModuleEditPermissions
            settingsDoc="timetable_profile_settings"
            moduleId="timetable_profiles"
            recordLabel="timetable versions"
          />
        </div>
      )}

      <div style={{ display: topTab === 'main' ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', border: isMobile ? 'none' : '1px solid var(--color-border)' }}>
          {profiles.length === 0 ? (
            <div className="p-4 text-muted" style={{ padding: '2rem', textAlign: 'center' }}>{t('timetableAdmin.empty')}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)' }}>
                  <th style={{ padding: '1rem', fontWeight: '600', width: '50px', borderRight: '1px solid var(--color-border)' }}>#</th>
                  {profiles.map(p => (
                    <th key={p.uid} style={{ padding: '1rem', fontWeight: '600', minWidth: '220px', borderRight: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ color: p.isDefault ? 'var(--color-success-text)' : 'var(--color-text-main)' }}>{p.name}</span>
                            {p.shortCode && <span style={{ fontSize: '0.7rem', backgroundColor: 'var(--color-bg-card)', padding: '0.1rem 0.3rem', borderRadius: '4px', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>{p.shortCode}</span>}
                          </div>
                          {p.isDefault && <div style={{ fontSize: '0.7rem', color: 'var(--color-success-text)', marginTop: '0.25rem', fontWeight: '600' }}>{t('timetableAdmin.defaultTimetable')}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button onClick={() => openEditor(p)} className="btn-outline" style={{ padding: '0.3rem' }}><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteProfile(p.uid)} className="btn-outline" style={{ padding: '0.3rem', color: 'var(--color-danger)' }}><Trash2 size={14} /></button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: Math.max(0, ...profiles.map(p => p.periods.length)) }).map((_, periodIndex) => (
                  <tr key={periodIndex} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '1rem', fontWeight: '700', borderRight: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                      {periodIndex + 1}
                    </td>
                    {profiles.map(p => {
                      const period = p.periods[periodIndex];
                      return (
                        <td key={p.uid} style={{ padding: '1rem', borderRight: '1px solid var(--color-border)', verticalAlign: 'top' }}>
                          {period ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              <div style={{ fontWeight: '600', color: 'var(--color-text-main)' }}>
                                {period.name} <span style={{ fontWeight: 'normal', color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>({periodTypeWord(period.type)})</span>
                              </div>
                              <div style={{ color: 'var(--color-primary)', fontSize: '0.8rem', fontWeight: '500' }}>
                                {period.startTime} - {period.endTime}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic', opacity: 0.5 }}>-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      {/* Profile editor -- a modal so the profile list stays visible behind it, rather than
          the editor replacing the entire page as it previously did. */}
      {isEditing && (
        <div
          onClick={() => setIsEditing(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 120,
            backgroundColor: 'var(--color-overlay)',
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
            padding: isMobile ? 0 : '2rem',
          }}
        >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            boxShadow: 'var(--shadow-lg)',
            width: '100%', maxWidth: isMobile ? '100%' : '900px',
            maxHeight: isMobile ? '92vh' : '90vh',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>{currentProfile.uid ? t('timetableAdmin.editProfile') : t('timetableAdmin.newProfile')}</h3>
            <button type="button" onClick={() => setIsEditing(false)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}><X size={20} /></button>
          </div>

          <form onSubmit={handleSaveProfile} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 200px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('timetableAdmin.profileName')}</label>
                <input type="text" className="input-field" value={currentProfile.name} onChange={e => setCurrentProfile({...currentProfile, name: e.target.value})} placeholder={t('timetableAdmin.profileNamePlaceholder')} required />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('timetableAdmin.shortCode')}</label>
                <input type="text" className="input-field" value={currentProfile.shortCode} onChange={e => setCurrentProfile({...currentProfile, shortCode: e.target.value})} placeholder={t('timetableAdmin.shortCodePlaceholder')} maxLength={8} required />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.75rem', marginLeft: 'auto' }}>
                <input type="checkbox" id="isDefault" checked={currentProfile.isDefault} onChange={e => setCurrentProfile({...currentProfile, isDefault: e.target.checked})} />
                <label htmlFor="isDefault" style={{ fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' }}>{t('timetableAdmin.defaultTimetable')}</label>
              </div>
            </div>

            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--color-text-muted)' }}>{t('timetableAdmin.dailyPeriods')}</h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
              {currentProfile.periods.map((p, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: 'min-content 2fr 110px min-content 110px 1fr min-content', gap: '0.5rem', alignItems: 'center', backgroundColor: 'var(--color-bg-app)', padding: '0.75rem', borderRadius: 'var(--radius-md)' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--color-text-muted)' }}>{idx + 1}.</span>
                  <input type="text" className="input-field" placeholder={t('timetableAdmin.periodName')} value={p.name} onChange={e => handleUpdatePeriod(idx, 'name', e.target.value)} required />
                  <input type="time" className="input-field" value={formatTimeForInput(p.startTime)} onChange={e => handleUpdatePeriod(idx, 'startTime', e.target.value)} required />
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem', fontWeight: '600' }}>to</span>
                  <input type="time" className="input-field" value={formatTimeForInput(p.endTime)} onChange={e => handleUpdatePeriod(idx, 'endTime', e.target.value)} required />
                  <input list="periodTypes" className="input-field" placeholder={t('timetableAdmin.type')} value={p.type} onChange={e => handleUpdatePeriod(idx, 'type', e.target.value)} required />
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button type="button" onClick={() => handleMovePeriod(idx, -1)} disabled={idx === 0} className="btn-outline" style={{ padding: '0.4rem', opacity: idx === 0 ? 0.3 : 1 }} title={t('timetableAdmin.moveUp')}>
                      <ArrowUp size={16} />
                    </button>
                    <button type="button" onClick={() => handleMovePeriod(idx, 1)} disabled={idx === currentProfile.periods.length - 1} className="btn-outline" style={{ padding: '0.4rem', opacity: idx === currentProfile.periods.length - 1 ? 0.3 : 1 }} title={t('timetableAdmin.moveDown')}>
                      <ArrowDown size={16} />
                    </button>
                    <button type="button" onClick={() => handleRemovePeriod(idx)} className="btn-outline" style={{ padding: '0.4rem', color: 'var(--color-danger)', border: '1px solid var(--color-danger)' }} title={t('timetableAdmin.deletePeriod')}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <datalist id="periodTypes">
              <option value="Lesson" />
              <option value="Break" />
              <option value="Admin" />
              <option value="AfterSchool" />
            </datalist>

            <button type="button" onClick={handleAddPeriod} className="btn-outline" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
              <Plus size={16} /> Add Period
            </button>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', borderTop: '1px solid var(--color-border)', paddingTop: '1.5rem' }}>
              <button type="button" onClick={() => setIsEditing(false)} className="btn-outline">{t('common.cancel')}</button>
              <button type="submit" className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Save size={16} /> Save Profile
              </button>
            </div>
          </form>
        </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TimetableAdmin;
